import { useEffect, useState } from 'react'
import { MeetingParticipant } from '../../modules/meeting/model'
import { getParticipantStatus } from '../../modules/meeting/model/meetingHelpers'
import { getInitials } from '../../shared/helpers/getInitials'

export interface ParticipantModalProps {
  selectedParticipant: MeetingParticipant | null
  closeModal: () => void
}

const ParticipantModal: React.FC<ParticipantModalProps> = ({ selectedParticipant, closeModal }) => {
  if (!selectedParticipant) {
    return null
  }

  const info = selectedParticipant.linkedInInfo
  const email = selectedParticipant.participantEmail
  const name = selectedParticipant.screenName || selectedParticipant.screenName || 'Unknown'
  const [personImageError, setPersonImageError] = useState(false)
  const [companyImageError, setCompanyImageError] = useState(false)
  const showPersonImage = Boolean(info?.person?.profile_pic_url && !personImageError)
  const showCompanyImage = Boolean(info?.company?.profile_pic_url && !companyImageError)
  const companyName = info?.company?.name || 'Company'

  useEffect(() => {
    setPersonImageError(false)
    setCompanyImageError(false)
  }, [selectedParticipant.participantUUID])

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
      onClick={closeModal}
    >
      <div
        className="relative max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          className="absolute right-4 top-4 rounded p-1 text-2xl text-gray-600 hover:bg-gray-100"
          onClick={closeModal}
        >
          ×
        </button>

        <div className="mb-8 flex items-start gap-5 pr-8">
          {showPersonImage ? (
            <img
              src={info?.person?.profile_pic_url}
              alt={name}
              className="h-24 w-24 rounded-full object-cover"
              onError={() => setPersonImageError(true)}
            />
          ) : (
            <div className="flex h-24 w-24 items-center justify-center rounded-full bg-blue-600 text-2xl font-medium text-white">
              {getInitials(name)}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <h2 className="mb-2 text-2xl font-semibold text-gray-900">{name}</h2>
            {info?.person?.headline && (
              <p className="mb-2 text-lg text-gray-900">{info.person.headline}</p>
            )}
            {email && <p className="mb-2 text-sm text-gray-600">{email}</p>}
            {info?.person?.location && (
              <p className="text-sm text-gray-600">
                {[info.person.city, info.person.state, info.person.country_full_name]
                  .filter(Boolean)
                  .join(', ')}
              </p>
            )}
          </div>
        </div>

        {info?.person && (
          <>
            {info.person.summary && (
              <div className="mt-8 border-t border-gray-200 pt-6">
                <h3 className="mb-4 text-lg font-semibold text-gray-900">About</h3>
                <p className="text-base text-gray-800">{info.person.summary}</p>
              </div>
            )}

            {info.person.experiences && info.person.experiences.length > 0 && (
              <div className="mt-8 border-t border-gray-200 pt-6">
                <h3 className="mb-4 text-lg font-semibold text-gray-900">Experience</h3>
                <div className="mb-6">
                  <h4 className="mb-1 text-base font-semibold text-gray-900">
                    {info.person.experiences[0].title}
                  </h4>
                  <p className="mb-2 text-sm text-gray-600">
                    {info.person.experiences[0].company}
                    {info.person.experiences[0].location &&
                      ` • ${info.person.experiences[0].location}`}
                  </p>
                  {info.person.experiences[0].description && (
                    <p className="mb-2 text-sm text-gray-800">
                      {info.person.experiences[0].description}
                    </p>
                  )}
                  {info.person.experiences[0].starts_at && (
                    <p className="text-sm text-gray-600">
                      {`${info.person.experiences[0].starts_at.month}/${info.person.experiences[0].starts_at.year}`}
                      {info.person.experiences[0].ends_at
                        ? ` - ${info.person.experiences[0].ends_at.month}/${info.person.experiences[0].ends_at.year}`
                        : ' - Present'}
                    </p>
                  )}
                </div>
              </div>
            )}

            {info.person.education && info.person.education.length > 0 && (
              <div className="mt-8 border-t border-gray-200 pt-6">
                <h3 className="mb-4 text-lg font-semibold text-gray-900">Education</h3>
                <div className="mb-6">
                  <h4 className="mb-1 text-base font-semibold text-gray-900">
                    {info.person.education[0].school}
                  </h4>
                  {info.person.education[0].degree_name && (
                    <p className="mb-1 text-sm text-gray-600">
                      {info.person.education[0].degree_name}
                    </p>
                  )}
                  {info.person.education[0].field_of_study && (
                    <p className="mb-1 text-sm text-gray-600">
                      {info.person.education[0].field_of_study}
                    </p>
                  )}
                  {info.person.education[0].starts_at && (
                    <p className="text-sm text-gray-600">
                      {`${info.person.education[0].starts_at.year}`}
                      {info.person.education[0].ends_at
                        ? ` - ${info.person.education[0].ends_at.year}`
                        : ' - Present'}
                    </p>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        {info?.company && (
          <div className="mt-8 border-t border-gray-200 pt-6">
            <h3 className="mb-4 text-lg font-semibold text-gray-900">Company Information</h3>
            <div className="flex flex-col gap-3">
              {showCompanyImage ? (
                <img
                  src={info?.company?.profile_pic_url}
                  alt={companyName}
                  className="h-16 w-16 rounded-md object-contain"
                  onError={() => setCompanyImageError(true)}
                />
              ) : (
                <div className="flex h-16 w-16 items-center justify-center rounded-md bg-gray-200 text-base font-semibold text-gray-700">
                  {getInitials(companyName)}
                </div>
              )}
              <h4 className="text-base font-semibold text-gray-900">{companyName}</h4>
              {info.company.tagline && (
                <p className="text-sm text-gray-600">{info.company.tagline}</p>
              )}
              {info.company.company_size_on_linkedin && (
                <p className="text-sm text-gray-600">
                  Size: {info.company.company_size_on_linkedin.toLocaleString()}+ employees
                </p>
              )}
              {info.company.industry && (
                <p className="text-sm text-gray-600">Industry: {info.company.industry}</p>
              )}
              {info.company.website && (
                <p className="text-sm text-gray-600">Website: {info.company.website}</p>
              )}
              {info.company.description && (
                <p className="text-sm text-gray-800">{info.company.description}</p>
              )}
            </div>
          </div>
        )}

        {!info && email && (
          <div className="mt-8 border-t border-gray-200 pt-6">
            <p className="text-base text-gray-800">
              {getParticipantStatus(selectedParticipant).text}
            </p>
          </div>
        )}

        {!info && !email && (
          <div className="mt-8 border-t border-gray-200 pt-6">
            <p className="text-base text-gray-800">
              No information available - email permission not granted
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
export default ParticipantModal
